import React, { useEffect, useState } from 'react';
import { Menu, X, ShoppingBag, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { LogoMark } from './LogoMark';
import { getShopUrl } from '../data/shopUrls';
export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close dropdowns on route change
  useEffect(() => {
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  }, [location]);
  const navStructure = [{
    name: 'Home',
    href: '/',
    type: 'link'
  }, {
    name: 'About Us',
    type: 'dropdown',
    items: [{
      name: 'Who We Are',
      href: '/about/who-we-are'
    }, {
      name: 'What We Do',
      href: '/about/what-we-do'
    }, {
      name: 'Certifications',
      href: '/about/certifications'
    }, {
      name: 'News and Insights',
      href: '/about/news'
    }]
  }, {
    name: 'Manufacturing',
    type: 'dropdown',
    items: [{
      name: 'Our Markets',
      href: '/manufacturing/markets'
    }, {
      name: 'Our Processes',
      href: '/manufacturing/processes'
    }, {
      name: 'Defence Procurement',
      href: '/manufacturing/defence-procurement'
    }, {
      name: 'Our Frameworks',
      href: '/manufacturing/frameworks'
    }, {
      name: 'Our Catalogues',
      href: '/manufacturing/catalogues'
    }]
  }, {
    name: 'Contact Us',
    href: '/contact',
    type: 'link'
  }];
  const isActive = (path: string) => {
    if (path === '/' && location.pathname !== '/') return false;
    return location.pathname.startsWith(path);
  };
  return <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || isMobileMenuOpen ? 'bg-navy-900 shadow-md border-b border-white/5 py-4' : 'bg-navy-900/90 backdrop-blur-sm py-6'}`} onMouseLeave={() => setActiveDropdown(null)} data-id="element-199">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-200">
        <div className="flex items-center justify-between" data-id="element-201">
          {/* Logo */}
          <div className="flex-shrink-0" data-id="element-202">
            <Link to="/" className="flex items-center" data-id="element-203">
              <LogoMark size="sm" variant="dark" data-id="element-204" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8" data-id="element-205">
            <div className="flex items-baseline space-x-6" data-id="element-206">
              {navStructure.map(item => <div key={item.name} className="relative group" data-id="element-207">
                  {item.type === 'link' ? <Link to={item.href!} className={`${isActive(item.href!) ? 'text-cg-orange-600' : 'text-steel-300 hover:text-white'} text-sm font-semibold transition-colors duration-200`} data-id="element-208">
                      {item.name}
                    </Link> : <button className={`text-steel-300 hover:text-white text-sm font-semibold transition-colors duration-200 flex items-center gap-1 focus:outline-none ${activeDropdown === item.name ? 'text-white' : ''}`} onMouseEnter={() => setActiveDropdown(item.name)} onClick={() => setActiveDropdown(activeDropdown === item.name ? null : item.name)} data-id="element-209">
                      {item.name} <ChevronDown className="w-3 h-3" data-id="element-210" />
                    </button>}

                  {/* Dropdown Menu */}
                  {item.type === 'dropdown' && activeDropdown === item.name && <div className="absolute left-0 mt-2 w-56 bg-navy-800 border border-white/10 rounded-sm shadow-xl py-2 z-50" onMouseEnter={() => setActiveDropdown(item.name)} data-id="element-211">
                      {item.items?.map(subItem => <Link key={subItem.name} to={subItem.href} className="block px-4 py-2 text-sm text-steel-300 hover:bg-white/5 hover:text-white transition-colors" data-id="element-212">
                          {subItem.name}
                        </Link>)}
                    </div>}
                </div>)}

              {/* Products Link - External */}
              <a href={getShopUrl()} target="_blank" rel="noopener noreferrer" className="text-steel-300 hover:text-white text-sm font-semibold transition-colors duration-200" data-id="element-213">
                Products
              </a>
            </div>

            <a href={getShopUrl()} target="_blank" rel="noopener noreferrer" className="bg-cg-orange-600 hover:bg-cg-orange-500 text-white px-5 py-2 rounded-sm text-sm font-bold uppercase tracking-wider transition-colors flex items-center gap-2" data-id="element-214">
              <ShoppingBag className="w-4 h-4" data-id="element-215" /> Inventory
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden" data-id="element-216">
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="inline-flex items-center justify-center p-2 rounded-md text-steel-300 hover:text-white focus:outline-none" data-id="element-217">
              <span className="sr-only" data-id="element-218">Open main menu</span>
              {isMobileMenuOpen ? <X className="block h-6 w-6" aria-hidden="true" data-id="element-219" /> : <Menu className="block h-6 w-6" aria-hidden="true" data-id="element-220" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && <div className="lg:hidden bg-navy-900 border-t border-white/10 absolute w-full shadow-xl max-h-[calc(100vh-80px)] overflow-y-auto" data-id="element-221">
          <div className="px-4 pt-4 pb-6 space-y-2" data-id="element-222">
            <a href={getShopUrl()} target="_blank" rel="noopener noreferrer" className="block px-3 py-3 rounded-md text-base font-bold text-white bg-cg-orange-600 hover:bg-cg-orange-500 mb-4 flex items-center gap-2" data-id="element-223">
              <ShoppingBag className="w-4 h-4" data-id="element-224" /> Browse Inventory
            </a>

            {navStructure.map(item => <div key={item.name} data-id="element-225">
                {item.type === 'link' ? <Link to={item.href!} onClick={() => setIsMobileMenuOpen(false)} className={`block px-3 py-3 rounded-md text-base font-medium ${isActive(item.href!) ? 'text-cg-orange-600 bg-white/5' : 'text-steel-300 hover:text-white hover:bg-white/5'}`} data-id="element-226">
                    {item.name}
                  </Link> : <div className="space-y-1" data-id="element-227">
                    <div className="px-3 py-3 text-base font-bold text-white border-b border-white/5 mb-2" data-id="element-228">
                      {item.name}
                    </div>
                    {item.items?.map(subItem => <Link key={subItem.name} to={subItem.href} onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 pl-6 rounded-md text-sm font-medium text-steel-400 hover:text-white hover:bg-white/5" data-id="element-229">
                        {subItem.name}
                      </Link>)}
                  </div>}
              </div>)}

            <a href={getShopUrl()} target="_blank" rel="noopener noreferrer" className="block px-3 py-3 rounded-md text-base font-medium text-steel-300 hover:text-white hover:bg-white/5" data-id="element-230">
              Products
            </a>
          </div>
        </div>}
    </nav>;
}